import { Component, useCallback, useEffect, useRef, useState } from 'react';
import ReactToPrint from 'react-to-print';
import cx from 'classnames';
import { Icon } from '@iconify/react';
import { connect, ConnectedProps } from 'react-redux';
import Spinner from 'react-spinkit';
import { selectClassroom } from '../Classrooms';
import { showIntercomNewMessage } from '../IntercomUtilityFunctions';
import { Segment, VerticalSpacer } from '../core/core';
import { fetchFromGallery } from '../BackendInterface';
import { getDescriptiveDateFromEpoch } from '../GlobalFunctions';
import ErrorDisplay from '../components/ErrorDisplay';
import { ugcCategories } from './GalleryDashboard';
import styles from '../SCSS/Gallery.module.scss';
import segmentStyles from '../SCSS/Segment.module.scss';
import utilityStyles from '../SCSS/common/utilityStyles.module.scss';
import libraryStyles from '../SCSS/Library.module.scss';
import { useCheckIfUserHasKeyFor } from '../lockDecorators';
import MarketingGallery from './MarketingGallery';
import { useParams } from 'react-router-dom';
import { RootState } from '../ApplicationState';
import { QueryOutput } from '@aws-sdk/client-dynamodb';

interface StudentProxy {
  userId: string;
  firstName: string;
  lastName: string;
}
interface FetchParameters {
  classroomId: string;
  batchSize: number;
  applicationFilter: string;
  lastEvaluatedKey?: Record<string, unknown>;
}
interface Image {
  userId: string;
  createdAt: number;
  mediainterface: string;
  charsetOrEncoding: string;
  content: string;
}
const GALLERY_AREA_ID = 'galleryArea';

const mapStateToProps = (state: RootState) => ({
  classroom: selectClassroom(state),
});

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux;

const Gallery = (props: Props): JSX.Element => {
  const [apiErrorMessage, setApiErrorMessage] = useState('');
  const [classroomOfLastFetch, setClassroomOfLastFetch] = useState(undefined);
  const [noImagesAvailable, setNoImagesAvailable] = useState(false);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [displayCount, setDisplayCount] = useState(4);
  const [isZoomVisible, setZoomVisible] = useState(false);
  const [zoomImageIndex, setZoomImageIndex] = useState(undefined);
  const [images, setImages] = useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(undefined); // empty = end of results

  const [isFetching, setIsFetching] = useState(false);
  const [placeholderDimensions, setPlaceholderDimensions] = useState(undefined);
  const params = useParams<{ ugcCategory: string }>();
  const applicationFilter = ugcCategories[params.ugcCategory].applicationFilter;
  const moreImagesToBeFetched = useCallback(
    () => lastEvaluatedKey !== undefined && Object.keys(lastEvaluatedKey).length > 0,
    [lastEvaluatedKey],
  );

  const startOverFromClassroomChange = () => {
    setApiErrorMessage('');
    setNoImagesAvailable(false);
    setDisplayIndex(0);
    setZoomVisible(false);
    setZoomImageIndex(undefined);
    setImages([]);
    setLastEvaluatedKey(undefined); // empty = end of results

    setIsFetching(false);
    setClassroomOfLastFetch(undefined);
  };

  if (classroomOfLastFetch && classroomOfLastFetch !== props.classroom.classroomId) {
    startOverFromClassroomChange();
  }

  const fetchSomeImages = (fetchParams: FetchParameters) => {
    return new Promise((resolve, reject) => {
      const { classroomId, batchSize, applicationFilter, lastEvaluatedKey } = fetchParams;
      fetchFromGallery(
        (data: QueryOutput) => {
          // const { Items, ...restOfData } = data;
          // console.debug( `fetch returned ${ JSON.stringify( restOfData, null, 2 ) }` );
          return resolve(data);
        },
        (error: { message: string }) => {
          reject(error.message);
        },
        classroomId,
        batchSize,
        applicationFilter,
        lastEvaluatedKey,
      );
    });
  };

  //
  // hook to fetch images
  //
  useEffect(() => {
    // don't start another fetch request if there's already one in progress
    if (isFetching) {
      return;
    }

    const enoughImagesAvailable = () => images.length - displayIndex >= 12;

    const alreadyFetched = () => lastEvaluatedKey !== undefined;

    if (!props.classroom) {
      // console.debug( `Skipping fetchFromGallery because Checked props.classroom and it's not ready` );
      return;
    }

    if (enoughImagesAvailable()) {
      // console.debug( `Skipping fetchFromGallery because next images are already loaded` );
      return;
    } else if (alreadyFetched() && !moreImagesToBeFetched()) {
      // console.debug( `Skipping fetchFromGallery there are no more images load` );
      return;
    }

    const classroomId = props.classroom.classroomId;

    const myFetch = async () => {
      try {
        setIsFetching(true);
        // console.debug( `Gallery - fetching images` );
        const fetchResults = await fetchSomeImages({
          classroomId,
          batchSize: 4,
          applicationFilter,
          lastEvaluatedKey,
        });

        if (fetchResults.Count > 0) {
          const newImages = [];
          fetchResults.Items.forEach((item) => {
            newImages.push(item);
          });
          setImages([...images, ...newImages]);
        } else {
          setNoImagesAvailable(true);
        }

        if (fetchResults.LastEvaluatedKey) {
          setLastEvaluatedKey(fetchResults.LastEvaluatedKey);
        } else {
          // indicate that there are no more results
          setLastEvaluatedKey({});
        }

        setIsFetching(false);
        setClassroomOfLastFetch(classroomId); // console.debug( `Gallery - done fetching images` );
      } catch (error) {
        console.error(`Gallery - error fetching images`);
        setIsFetching(false);
        setApiErrorMessage(error);
      }
    };

    myFetch();
  }, [
    displayIndex,
    props.classroom,
    noImagesAvailable,
    apiErrorMessage,
    isFetching,
    applicationFilter,
    images,
    lastEvaluatedKey,
    moreImagesToBeFetched,
  ]);
  //
  // hook to measure dimensions of image box for use
  // in generating spinner placeholders
  //
  useEffect(() => {
    if (isFetching) return;
    const galleryArea = document.getElementById(GALLERY_AREA_ID);

    if (galleryArea) {
      const figureContainers = galleryArea.getElementsByClassName(styles.figureContainer);

      if (figureContainers) {
        const dims = {
          height: figureContainers[0].clientHeight,
          width: figureContainers[0].clientWidth,
        };
        setPlaceholderDimensions(dims);
      }
    }
  }, [isFetching]);

  const updateImageCount = (count) => {
    //
    // re: setDisplayIndex
    // In a set of 16 images altogether,
    // EX1 if the user is seeing frame [ 12, 15 ] in 4-up
    // and then switches to 1-up, then they should see [ 12, 12]
    // EX2 if user is seeing frame [ 13, 13 ] in 1-up
    // and then switches to 4-up, then they should see [ 12, 15]
    //
    // We don't want a case where by switching format, the user
    // will see a frame like [ 13, 16 ] where 16 doesn't exist or
    // [ 2, 5 ] and can't go back to see images 0 and 1 because
    // of bounds checking
    setDisplayIndex(displayIndex - (displayIndex % count));
    setDisplayCount(count);
  };

  const goPrevious = () => {
    if (displayIndex - displayCount >= 0) {
      setDisplayIndex(displayIndex - displayCount);
    }
  };

  const goNext = () => {
    // NB: isFetching - if we don't check whether there's a current fetch
    // we could allow the display index to advance beyond the number of
    // available images because lastEvaluatedKey hasn't been updated from
    // the current fetch yet.
    if (
      displayIndex + displayCount < images.length ||
      (lastEvaluatedKey && Object.keys(lastEvaluatedKey).length > 0 && !isFetching)
    ) {
      setDisplayIndex(displayIndex + displayCount);
    }
  };

  const currentImage = images[displayIndex];
  const imagesReady = currentImage !== undefined;

  const hideZoomOverlay = () => setZoomVisible(false);

  const ImageCountSelector = (props: { onUpdateCount: (arg0: number) => void; currentCount: number }) => {
    const [selection, setSelection] = useState(props.currentCount);
    return (
      <div className={styles.imageCountSelector}>
        <div
          className={cx({
            [styles.selectorButton]: true,
            [styles.selected]: selection === 1,
          })}
          onClick={() => {
            setSelection(1);
            props.onUpdateCount(1);
          }}
        >
          <Icon icon='ic:round-crop-square' />
        </div>
        <div
          className={cx({
            [styles.selectorButton]: true,
            [styles.selected]: selection === 2,
          })}
          onClick={() => {
            setSelection(2);
            props.onUpdateCount(2);
          }}
        >
          <Icon icon='mingcute:columns-3-line' />
        </div>
        <div
          className={cx({
            [styles.selectorButton]: true,
            [styles.selected]: selection === 4,
          })}
          onClick={() => {
            setSelection(4);
            props.onUpdateCount(4);
          }}
        >
          <Icon icon='material-symbols:grid-view-outline-rounded' />
        </div>
      </div>
    );
  };

  const onZoom = (zoomImageIndex) => {
    setZoomImageIndex(zoomImageIndex);
    setZoomVisible(true);
  };

  class ZoomImage extends Component<Record<string, unknown>> {
    render() {
      return (
        <>
          {getImages({
            images,
            students: props.classroom.listOfStudentProxies,
            displayIndex: zoomImageIndex === undefined ? 0 : zoomImageIndex,
            displayCount: 1,
            isZoomable: false,
          })}
        </>
      );
    }
  }

  const ZoomOverlay = () => {
    const componentRef = useRef();
    if (!isZoomVisible) return null;
    return (
      <div className={styles.zoomOverlay}>
        <div className={styles.zoomButtons}>
          <ReactToPrint
            trigger={() => (
              <div className={styles.printButton}>
                <i className='fa fa-print'></i>
              </div>
            )}
            content={() => componentRef.current}
          />
          <div className={styles.closeButton} onClick={hideZoomOverlay}>
            <i className='fa fa-times'></i>
          </div>
        </div>
        <ZoomImage ref={componentRef} />
      </div>
    );
  };

  const locked = !useCheckIfUserHasKeyFor('KEY_MYPEEKAVILLE_LESSONS');
  if (locked) {
    return (
      <MarketingGallery
        contentAsNoun={ugcCategories[params.ugcCategory].title}
        screenshotImageUrl={ugcCategories[params.ugcCategory].screenshotImage}
      />
    );
  }

  return (
    <div className={styles.gallery}>
      <ZoomOverlay />
      <VerticalSpacer ems={2} />
      <Segment className={utilityStyles.line}>
        <div className={styles.titleBar}>
          <div className={segmentStyles.moduleTitle}>Gallery - {ugcCategories[params.ugcCategory].title}</div>
          <ImageCountSelector onUpdateCount={updateImageCount} currentCount={displayCount} />
        </div>
      </Segment>
      <Segment>
        {imagesReady ? (
          <div className={styles.galleryController}>
            <div className={styles.galleryArea} id={GALLERY_AREA_ID}>
              {getImages({
                images,
                students: props.classroom.listOfStudentProxies,
                displayIndex,
                displayCount,
                moreImagesToBeFetched: moreImagesToBeFetched(),
                placeholderDimensions,
                isZoomable: true,
                zoomHandler: onZoom,
              })}
            </div>
            <div className={styles.controlButtons}>
              <div className={styles.chevron} onClick={goPrevious}>
                <i className={cx('fa fa-chevron-left', styles.chevron)} aria-hidden='true' />
              </div>
              <div className={styles.chevron} onClick={goNext}>
                <i className={cx('fa fa-chevron-right', styles.chevron)} aria-hidden='true' />
              </div>
            </div>
          </div>
        ) : apiErrorMessage ? (
          <>
            <ErrorDisplay
              contentTitle='Uh oh...'
              contentSubtitle={() => (
                <div>
                  We&apos;re sorry, we couldn&apos;t get the pictures. Please{' '}
                  <span className={libraryStyles.textLink} onClick={() => showIntercomMessage(apiErrorMessage)}>
                    contact Peekapak
                  </span>{' '}
                  and let them know that there&apos;s a problem, and that the error condition is:{' '}
                  <p className={styles.technicalErrorMessage}>
                    <strong>{apiErrorMessage}</strong>
                  </p>
                </div>
              )}
              buttonText='Try Again'
              onButtonClick={() => {
                setApiErrorMessage('');
              }}
            />
          </>
        ) : noImagesAvailable ? (
          <>
            <ErrorDisplay
              contentTitle='Sorry!'
              contentSubtitle={() => (
                <div>Seems like no one&apos;s created any artwork yet! Please check again later.</div>
              )}
              buttonText='Try Again'
              onButtonClick={() => {
                setNoImagesAvailable(false);
                setLastEvaluatedKey(undefined);
              }}
            />
          </>
        ) : (
          <div className={cx(styles.galleryController, styles.centreSpinner)}>
            <div className={styles.galleryArea}>
              <Spinner className={styles.spinnerPlaceholder} name='three-bounce' />
            </div>
          </div>
        )}
      </Segment>
    </div>
  );
};

function showIntercomMessage(message: string) {
  showIntercomNewMessage(`Problem report: ${message}`);
}

type GetImagesParams = {
  images: Array<Image>;
  students: Array<StudentProxy>;
  displayIndex: number;
  displayCount: number;
  moreImagesToBeFetched?: boolean;
  placeholderDimensions?: {
    height: number;
    width: number;
  };
  isZoomable?: boolean;
  zoomHandler?: (arg0: number) => void;
};

function getImages(params: GetImagesParams) {
  const filtered = [];
  const {
    images,
    students,
    displayIndex,
    displayCount,
    moreImagesToBeFetched,
    placeholderDimensions,
    isZoomable,
    zoomHandler,
  } = params;
  // console.debug( `getting images [ ${ displayIndex }, ${ displayIndex + displayCount - 1 } ] of ${ images.length } images` );
  images.forEach((image, index) => {
    if (index >= displayIndex && index < displayIndex + displayCount) {
      filtered.push(
        <div
          key={`${image.userId}${image.createdAt}`}
          className={styles.figureContainer}
          data-count={`${displayCount}`}
        >
          {getImageElement(image, isZoomable, zoomHandler, index)}
          <div className={styles.imageCaption}>
            Created by {getStudentsName(students, image.userId)} {getDescriptiveDateFromEpoch(image.createdAt)}
          </div>
        </div>,
      );
    }
  });

  if (moreImagesToBeFetched) {
    const readyImages = filtered.length;
    const keyroot = `${Date.now()}`;
    const { height, width } = placeholderDimensions
      ? placeholderDimensions
      : {
          height: 0,
          width: 0,
        };
    let i = 0;

    while (i < displayCount - readyImages) {
      filtered.push(
        <div
          key={`${keyroot}_${i}`}
          className={styles.spinnerPlaceholderContainer}
          style={{
            height,
            width,
          }}
        >
          <Spinner className={styles.spinnerPlaceholder} name='three-bounce' />
        </div>,
      );
      i += 1;
    }
  }

  return filtered;

  function getImageElement(image, isZoomable, zoomHandler, zoomImageIndex) {
    const imageProps = {
      className: styles.galleryImage,
      src: `data:${image.mediaType};${image.charsetOrEncoding},${image.content}`,
      onClick: undefined,
    };

    if (isZoomable && zoomHandler) {
      imageProps['onClick'] = () => zoomHandler(zoomImageIndex);

      imageProps.className = `${imageProps.className} ${styles.zoomable}`;
    }

    return <img alt='student art' {...imageProps} />;
  }
}

function getStudentsName(list: Array<StudentProxy>, userId: string) {
  if (!list) return 'UNKNOWN';
  let i = 0;

  while (i < list.length) {
    if (list[i].userId === userId) {
      return `${list[i].firstName}${list[i].lastName ? ' ' + list[i].lastName : ''}`;
    }

    i += 1;
  }

  return 'UNKNOWN';
}

export default connector(Gallery);
